import { APP_NAME } from '../brand';

/**
 * Textos de la interfaz, equivalentes a `res/values/strings.xml` del cliente Android.
 *
 * Centralizarlos mantiene la propiedad que sostiene el resto del diseño: los errores y las
 * validaciones viajan por la app como **valores** (`AppError`, `FieldError`) y solo se
 * convierten en texto aquí, en el borde de la presentación.
 */
export const S = {
  appName: APP_NAME,

  // Navegación
  navHome: 'Inicio',
  navSearch: 'Buscar',
  navReservations: 'Reservas',
  navProfile: 'Perfil',
  back: 'Volver',

  // Inicio
  homeGreeting: '¿Dónde quieres comer?',
  homeSearchHint: 'Restaurante, cocina o zona',
  homeSectionRecommended: 'Recomendados para ti',
  homeSectionNearby: 'Cerca de ti',
  homeSectionFeatured: 'Destacados',
  homeMyReservations: 'Mis reservas',
  homeEmpty: 'Aún no hay recomendaciones en esta zona',
  homeEmptyAction: 'Cambiar ubicación',

  // Búsqueda
  searchTitle: 'Buscar',
  searchFilters: 'Filtros',
  searchSort: 'Ordenar',
  searchClearFilters: 'Quitar filtros',
  searchResultsCount: (n: number) => `${n} ${n === 1 ? 'restaurante' : 'restaurantes'}`,
  searchEmptyTitle: 'Sin resultados',
  searchEmptyBody: 'No encontramos restaurantes con esos criterios.',
  searchEmptySuggestionTime: 'Probar otro horario',

  sortRelevance: 'Relevancia',
  sortRating: 'Mejor calificados',
  sortDistance: 'Más cerca',
  sortPriceLow: 'Precio: menor a mayor',
  sortPriceHigh: 'Precio: mayor a menor',

  // Selector fecha/hora/personas
  pickerDate: 'Fecha',
  pickerTime: 'Hora',
  pickerParty: 'Personas',
  pickerApply: 'Aplicar',
  pickerLocation: 'Ubicación',
  filtersCuisine: 'Tipo de cocina',
  filtersPrice: 'Precio',
  filtersRating: 'Calificación mínima',
  filtersDistance: 'Distancia máxima',
  filtersAmenities: 'Servicios',
  filtersOnlyAvailable: 'Solo con mesa disponible',
  filtersApply: 'Ver resultados',
  filtersAny: 'Cualquiera',

  // Restaurante
  restaurantReviews: (n: number) => `${n} ${n === 1 ? 'reseña' : 'reseñas'}`,
  restaurantAbout: 'Acerca de',
  restaurantHours: 'Horarios',
  restaurantHoursUnavailable: 'Horarios no disponibles',
  restaurantAmenities: 'Servicios',
  restaurantContact: 'Contacto',
  restaurantCall: 'Llamar',
  restaurantMenu: 'Ver menú',
  restaurantMap: 'Ver en el mapa',
  restaurantOpenNow: 'Abierto ahora',
  restaurantClosedNow: 'Cerrado ahora',
  restaurantClosedToday: 'Cerrado este día',
  restaurantBook: 'Reservar',
  restaurantPhoto: (name: string) => `Foto de ${name}`,
  restaurantNoPhoto: 'Sin fotografía disponible',
  restaurantDistance: (value: string) => `A ${value}`,
  restaurantReviewsSection: 'Reseñas',
  restaurantNoReviews: 'Todavía no hay reseñas',

  // Disponibilidad
  availabilityTitle: 'Disponibilidad',
  availabilityNoneTitle: 'Sin mesas para ese horario',
  availabilityNoneBody: 'Prueba con otra hora o cambia el número de personas.',
  availabilityAlternatives: 'Horarios alternativos',
  alternativeNearbyTime: 'Otra hora',
  alternativeAnotherDay: 'Otro día',
  alternativeSmallerParty: 'Grupo más pequeño',

  // Reserva
  bookingStepSlot: 'Fecha y hora',
  bookingStepDetails: 'Tus datos',
  bookingStepReview: 'Confirmar',
  bookingContinue: 'Continuar',
  bookingConfirm: 'Confirmar reserva',
  bookingName: 'Nombre completo',
  bookingPhone: 'Teléfono',
  bookingEmail: 'Correo electrónico',
  bookingOccasion: 'Ocasión (opcional)',
  bookingNotes: 'Peticiones especiales (opcional)',
  bookingNotesCounter: (n: number) => `${n}/500`,
  bookingTerms: 'Acepto las condiciones de la reserva',
  bookingTermsBody:
    'Puedes cancelar sin costo hasta 2 horas antes. El restaurante mantiene la mesa 15 minutos.',
  bookingLoginRequired: 'Inicia sesión para confirmar tu reserva',
  bookingOffline: 'Necesitas conexión para reservar',
  bookingSummary: 'Resumen',
  bookingEditing: 'Estás modificando una reserva existente',

  // Confirmación
  confirmationTitle: '¡Reserva confirmada!',
  confirmationTitleModified: 'Reserva actualizada',
  confirmationCode: (code: string) => `Código: ${code}`,
  confirmationAddCalendar: 'Añadir al calendario',
  confirmationViewReservations: 'Ver mis reservas',
  confirmationDone: 'Listo',

  // Mis reservas
  reservationsTitle: 'Mis reservas',
  reservationsTabUpcoming: 'Próximas',
  reservationsTabPast: 'Anteriores',
  reservationsTabCancelled: 'Canceladas',
  reservationsEmptyUpcoming: 'No tienes reservas próximas',
  reservationsEmptyPast: 'Aún no tienes reservas anteriores',
  reservationsEmptyCancelled: 'No has cancelado ninguna reserva',
  reservationsEmptyAction: 'Buscar restaurantes',
  reservationModify: 'Modificar',
  reservationCancel: 'Cancelar reserva',
  reservationCancelConfirmTitle: '¿Cancelar la reserva?',
  reservationCancelConfirmBody: (restaurant: string) =>
    `Se liberará tu mesa en ${restaurant}. Esta acción no se puede deshacer.`,
  reservationCancelConfirmYes: 'Sí, cancelar',
  reservationCancelConfirmNo: 'No',
  reservationCancelNotAllowed: (hours: number) => `No cancelable a menos de ${hours} h`,
  reservationViewRestaurant: 'Ver restaurante',
  reservationShare: 'Compartir',
  reservationGuest: 'A nombre de',
  reservationOccasion: 'Ocasión',
  reservationNotes: 'Peticiones especiales',
  reservationStatusConfirmed: 'Confirmada',
  reservationStatusCancelled: 'Cancelada',
  reservationStatusCompleted: 'Completada',
  reservationStatusNoShow: 'No asististe',

  // Perfil / sesión
  profileTitle: 'Perfil',
  profilePersonal: 'Información personal',
  profilePreferences: 'Preferencias',
  profileSettings: 'Configuración',
  profileLogout: 'Cerrar sesión',
  profileGuestTitle: 'No has iniciado sesión',
  profileGuestBody: 'Inicia sesión para guardar tus reservas y preferencias.',
  profileFavoriteCuisines: 'Cocinas favoritas',
  profileDefaultParty: 'Personas por defecto',
  profileTheme: 'Tema',
  profileThemeSystem: 'Del sistema',
  profileThemeLight: 'Claro',
  profileThemeDark: 'Oscuro',
  authLogin: 'Identifícate',
  authTitle: 'Identifícate para reservar',
  authSubtitleTelegram:
    'Confirmamos tu teléfono por Telegram, sin que tengas que escribirlo. Es gratis y toma unos segundos.',
  authOpenTelegram: 'Continuar en Telegram',
  authStep1: 'Se abre Telegram con nuestro bot.',
  authStep2: 'Pulsa «Iniciar».',
  authStep3: 'Pulsa «Compartir mi teléfono».',
  authWaiting: 'Esperando la confirmación de Telegram…',
  authLinkExpired: 'El enlace caducó. Pide uno nuevo.',
  authRetry: 'Empezar de nuevo',
  authVerified: 'Teléfono verificado',
  authNoTelegram: '¿No tienes Telegram?',
  authNoTelegramBody:
    'Por ahora la verificación solo funciona por Telegram, que es gratis. Enviar un SMS exige contratar un proveedor de pago.',
  authDemoNotice:
    'Demo sin servidor: no hay bot de verdad. La verificación se aprueba sola en unos segundos para que puedas seguir.',
  authUnverifiedWarning:
    'Este servidor todavía no tiene el bot configurado, así que tu teléfono no se verificará. No lo uses con datos reales.',
  authContinue: 'Continuar',
  authContinueGuest: 'Seguir explorando sin cuenta',
  authPhoneHint: 'Diez dígitos. El restaurante te llamará a este número si hace falta.',
  authPhoneConfirm: (phone: string) => `Entrarás como ${phone}`,
  authDemoHint: 'Demo: no se envía ningún código de verificación.',

  // Errores
  errorNetworkTitle: 'Sin conexión',
  errorNetworkBody: 'Revisa tu conexión e inténtalo de nuevo.',
  errorOfflineBody: 'Estás sin conexión. Te mostramos la última información guardada.',
  errorTimeoutTitle: 'La conexión tardó demasiado',
  errorServerTitle: 'Algo salió mal',
  errorServerBody: 'Estamos teniendo problemas. Inténtalo en un momento.',
  errorNotFoundTitle: 'No encontramos esta información',
  errorSlotTakenTitle: 'Esa mesa se acaba de ocupar',
  errorSlotTakenBody: 'Elige otro horario disponible.',
  errorSessionExpiredTitle: 'Tu sesión expiró',
  errorSessionExpiredBody: 'Inicia sesión de nuevo para continuar.',
  errorRateLimitedTitle: 'Demasiados intentos',
  errorUnknownTitle: 'Error inesperado',
  errorValidationTitle: 'Revisa los datos',
  actionRetry: 'Reintentar',
  actionChangeTime: 'Cambiar horario',
  actionLogin: 'Iniciar sesión',
  actionDismiss: 'Entendido',

  // Validaciones
  validationNameRequired: 'Escribe tu nombre',
  validationNameLength: 'El nombre debe tener entre 2 y 60 caracteres',
  validationPhoneRequired: 'Escribe tu teléfono',
  validationPhoneFormat: 'El teléfono debe tener 10 dígitos',
  validationEmailRequired: 'Escribe tu correo electrónico',
  validationEmailFormat: 'Revisa tu correo electrónico',
  validationDatePast: 'Elige una fecha a partir de hoy',
  validationDateTooFar: 'Solo puedes reservar con 90 días de anticipación',
  validationTimePast: 'Ese horario ya pasó',
  validationTimeClosed: 'El restaurante está cerrado a esa hora',
  validationPartyMin: 'Elige al menos 1 persona',
  validationPartyMax: 'Para más de 12 personas, contacta al restaurante',
  validationNotesLength: 'Máximo 500 caracteres',
  validationTermsRequired: 'Acepta las condiciones para continuar',
  validationSlotRequired: 'Elige un horario disponible',
  validationUnknown: 'Revisa este campo',

  // Genéricos / accesibilidad
  loading: 'Cargando',
  ratingLabel: (value: string) => `Calificación ${value} de 5`,
  cdOpenFilters: 'Abrir filtros',
  cdProfile: 'Abrir perfil',
  cdClear: 'Limpiar',
  demoBanner: 'Demo sin servidor: los datos son simulados en el navegador.',
} as const;
